import { useCallback, useEffect, useRef } from 'react';
import { animate, useMotionValue, useReducedMotion } from 'framer-motion';
import { MAGNETIC_TWEEN } from './motionConfig';

/**
 * Damped magnetic pull (tween only — no springs).
 * Disabled on touch / coarse pointers and when prefers-reduced-motion is set.
 */
export default function useMagneticEffect({ strength = 0.25, maxOffset = 7 } = {}) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const reduced = useReducedMotion();

  const reset = useCallback(() => {
    animate(x, 0, MAGNETIC_TWEEN);
    animate(y, 0, MAGNETIC_TWEEN);
  }, [x, y]);

  const onMove = useCallback(
    (event) => {
      if (reduced || !ref.current) return;
      const el = ref.current;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      let dx = ((event.clientX - cx) / rect.width) * 2 * maxOffset * strength;
      let dy = ((event.clientY - cy) / rect.height) * 2 * maxOffset * strength;
      dx = Math.max(-maxOffset, Math.min(maxOffset, dx));
      dy = Math.max(-maxOffset, Math.min(maxOffset, dy));
      animate(x, dx, MAGNETIC_TWEEN);
      animate(y, dy, MAGNETIC_TWEEN);
    },
    [maxOffset, reduced, strength, x, y]
  );

  useEffect(() => {
    if (reduced) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const noHover = window.matchMedia('(hover: none)');
    if (noHover.matches) return undefined;

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', reset);
    el.addEventListener('pointercancel', reset);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', reset);
      el.removeEventListener('pointercancel', reset);
    };
  }, [onMove, reduced, reset]);

  return { ref, x, y };
}
